import React from "react";
import PlateSearch from "./plate-search";
import Link from "next/link";
import dynamic from "next/dynamic";

// Import VehicleSearch dynamically with SSR disabled to avoid HTMLElement issues
const VehicleSearch = dynamic(() => import("@cartbot/vehicle-search-beta"), {
  ssr: false,
  loading: () => <div className="h-12 bg-gray-100 animate-pulse rounded"></div>,
});

import { motion } from "framer-motion";
import { titleInitial, titleAnimate } from "@/components/animations";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PuzzlePieceIcon } from "@phosphor-icons/react";

export default function VehicleSearchDemo() {
  return (
    <>
      <div className="relative overflow-x-hidden">
        <div className="container mx-auto">
          <div className="px-6 lg:py-8">
            <div>
              <div className="mb-4 flex items-center gap-2 md:justify-center">
                <PuzzlePieceIcon
                  className="fa-swap-opacity h-4 w-4 text-amber-500"
                  aria-hidden="true"
                />
                <Badge variant="secondary">Plugin</Badge>
              </div>
              <div className="mt-6">
                <motion.h2
                  initial={titleInitial}
                  whileInView={titleAnimate()}
                  viewport={{ once: true }}
                  className="mx-auto max-w-3xl text-balance bg-linear-to-r from-stone-300 via-white to-stone-300 bg-clip-text font-sans text-4xl font-extrabold leading-none tracking-tight text-transparent md:text-center md:text-6xl"
                >
                  Supercharged Vehicle Search
                </motion.h2>

                <motion.div
                  initial={titleInitial}
                  whileInView={titleAnimate(0.5)}
                  viewport={{ once: true }}
                  className="mt-6 flex w-full flex-col md:mt-12 md:items-center md:justify-center"
                >
                  <div className="prose text-balance text-emerald-300/75 md:text-center md:text-lg">
                    <p>
                      Partbot&apos;s{" "}
                      <a
                        href="https://mmyvs.partbot.io"
                        target="_blank"
                        className="text-emerald-300 underline decoration-emerald-300/60 transition hover:text-emerald-200"
                      >
                        Make, Model, Year Vehicle Search
                      </a>{" "}
                      is hands-down the fastest vehicle search plugin available
                      for auto parts ecommerce stores. Style to match the look
                      of your website and configure to show as many or as little
                      fields as you need.
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={titleInitial}
                  whileInView={titleAnimate(0.75)}
                  viewport={{ once: true }}
                  className="shadow-2x partbot-mmyvs partbot-mmyvs-scope mt-12 w-full rounded-xl border border-stone-300/10 bg-stone-950/80 p-4 lg:p-8"
                >
                  <VehicleSearch
                    apiKey={process.env.NEXT_PUBLIC_PARTBOT_API_KEY || ""}
                    fields="body,series"
                    showBranding={false}
                    showLabels={false}
                    callback={false}
                    enableFeatureFlags={true}
                  />
                </motion.div>
              </div>
            </div>
          </div>
          <div className="mt-12 md:mt-16 lg:mt-0">
            <div className="-mr-48 pl-4 sm:pl-6 md:-mr-16 lg:relative lg:m-0 lg:h-full lg:px-0"></div>
          </div>
        </div>

        <div className="container mx-auto">
          <div className="mx-auto lg:mx-0 lg:max-w-none lg:py-16 xl:col-start-2">
            <div>
              <div className="mt-20 grid grid-cols-1 md:px-6 lg:grid-cols-2">
                <div className="px-6 pr-8 md:px-0">
                  <div className="mb-4 flex items-center gap-2">
                    <PuzzlePieceIcon
                      className="fa-swap-opacity h-4 w-4 text-amber-500"
                      aria-hidden="true"
                    />
                    <Badge variant="secondary">Plugin</Badge>
                  </div>
                  <motion.h2
                    initial={titleInitial}
                    whileInView={titleAnimate(0.25)}
                    className="text-balance bg-linear-to-r from-stone-300 via-white to-stone-300 bg-clip-text pb-1 pr-6 text-3xl font-bold tracking-tight text-transparent lg:text-6xl"
                  >
                    Number Plate Search
                  </motion.h2>
                  <motion.p
                    initial={titleInitial}
                    whileInView={titleAnimate(0.5)}
                    className="prose mt-4 max-w-prose text-balance text-emerald-300/75 md:text-lg"
                  >
                    Add an Australian vehicle registration search to your auto
                    parts ecommerce store. Customisable to match your shop&apos;s
                    theme.
                  </motion.p>
                  <motion.div
                    initial={titleInitial}
                    whileInView={titleAnimate(0.75)}
                    className="hidden items-center space-x-6 pt-12 md:flex"
                  >
                    <Button asChild className="bg-emerald-500">
                      <Link href="/pricing">View Pricing</Link>
                    </Button>
                    <p className="text-xs leading-none text-stone-300 lg:text-sm">
                      Available via API or plugin.
                    </p>
                  </motion.div>
                </div>
                <motion.div
                  initial={{ x: 110, opacity: 0 }}
                  whileInView={{
                    x: 0,
                    opacity: 1,
                    transition: {
                      delay: 0.25,
                      duration: 2,
                      ease: "easeOut",
                    },
                  }}
                  className="mt-8 flex items-start justify-end px-0 lg:-mt-12 lg:pl-16"
                >
                  <div className="relative w-full max-w-2xl">
                    <div className="pointer-events-none absolute -right-2 -top-10 z-20 size-20 md:-right-8 md:-top-10 md:size-24">
                      <span className="absolute inset-0 h-full w-full animate-ping rounded-full bg-amber-400 opacity-10"></span>
                      <div className="absolute inset-0 z-10 flex aspect-square size-20 rotate-12 transform items-center justify-center rounded-full bg-amber-500 md:size-24">
                        <span className="text-center text-lg font-extrabold leading-none text-primary-foreground md:text-xl">
                          Try me!
                        </span>
                      </div>
                    </div>
                    <PlateSearch
                      displayMode="inline"
                      inlineAlign="end"
                      demoMode={true}
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
